import React, { useState, useEffect } from 'react';
import { DollarSign, Clock, AlertTriangle } from 'lucide-react';
import {
  BillingCodeType,
  PlaceOfService,
  LocationModifier,
  BILLING_CODES,
  PLACE_OF_SERVICE,
  calculateUnits,
  calculateDuration,
} from '../models/BillingCode';

interface BillingEntryProps {
  providerName: string;
  providerCredentials: string;
  sessionStartTime?: Date;
  sessionEndTime?: Date;
  onComplete: (billingData: {
    code: BillingCodeType;
    placeOfService: PlaceOfService;
    locationModifier?: LocationModifier;
    startTime: Date;
    endTime: Date;
    duration: number;
    unitsOfService: number;
  }) => void;
  initialData?: {
    code?: BillingCodeType;
    placeOfService?: PlaceOfService;
    locationModifier?: LocationModifier;
    startTime?: Date;
    endTime?: Date;
  };
}

export const BillingEntry: React.FC<BillingEntryProps> = ({
  providerName,
  providerCredentials,
  sessionStartTime,
  sessionEndTime,
  onComplete,
  initialData,
}) => {
  const [billingCode, setBillingCode] = useState<BillingCodeType>(
    initialData?.code || '97155'
  );
  const [placeOfService, setPlaceOfService] = useState<PlaceOfService>(
    initialData?.placeOfService || '10'
  );
  const [locationModifier, setLocationModifier] = useState<LocationModifier | undefined>(
    initialData?.locationModifier
  );
  const [startTime, setStartTime] = useState<string>(
    initialData?.startTime
      ? `${initialData.startTime.getHours().toString().padStart(2, '0')}:${initialData.startTime.getMinutes().toString().padStart(2, '0')}`
      : sessionStartTime
      ? `${sessionStartTime.getHours().toString().padStart(2, '0')}:${sessionStartTime.getMinutes().toString().padStart(2, '0')}`
      : ''
  );
  const [endTime, setEndTime] = useState<string>(
    initialData?.endTime
      ? `${initialData.endTime.getHours().toString().padStart(2, '0')}:${initialData.endTime.getMinutes().toString().padStart(2, '0')}`
      : sessionEndTime
      ? `${sessionEndTime.getHours().toString().padStart(2, '0')}:${sessionEndTime.getMinutes().toString().padStart(2, '0')}`
      : ''
  );

  const [duration, setDuration] = useState<number>(0);
  const [units, setUnits] = useState<number>(0);
  const [showWarning, setShowWarning] = useState(false);

  // Calculate duration and units when times change
  useEffect(() => {
    if (startTime && endTime) {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const start = new Date();
      start.setHours(startHour, startMin, 0, 0);

      const end = new Date();
      end.setHours(endHour, endMin, 0, 0);

      const calculatedDuration = calculateDuration(start, end);
      const calculatedUnits = calculateUnits(calculatedDuration);

      setDuration(calculatedDuration);
      setUnits(calculatedUnits);
    }
  }, [startTime, endTime]);

  const handleSubmit = () => {
    if (!billingCode || !placeOfService || !startTime || !endTime) {
      setShowWarning(true);
      return;
    }

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const start = new Date();
    start.setHours(startHour, startMin, 0, 0);

    const end = new Date();
    end.setHours(endHour, endMin, 0, 0);

    if (end <= start) {
      setShowWarning(true);
      return;
    }

    onComplete({
      code: billingCode,
      placeOfService,
      locationModifier,
      startTime: start,
      endTime: end,
      duration,
      unitsOfService: units,
    });
  };

  const selectedCodeInfo = BILLING_CODES[billingCode];
  const isTelehealth = placeOfService === '02' || placeOfService === '10';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <DollarSign className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-bold text-green-900">Billing Information</h3>
            <p className="text-sm text-green-700 mt-1">
              Enter billing code and service details for this supervision session
            </p>
          </div>
        </div>
      </div>

      {/* Provider Info */}
      <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-700">Provider</label>
            <div className="text-sm font-bold text-gray-900">{providerName}</div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Credentials</label>
            <div className="text-sm font-bold text-gray-900">{providerCredentials}</div>
          </div>
        </div>
      </div>

      {/* Billing Code Selection */}
      <div>
        <label className="block text-sm font-bold text-gray-900 mb-2">
          Authorization / Billing Code *
        </label>
        <select
          value={billingCode}
          onChange={(e) => setBillingCode(e.target.value as BillingCodeType)}
          className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          {Object.entries(BILLING_CODES).map(([code, info]) => (
            <option key={code} value={code}>
              {code}: {info.description}
            </option>
          ))}
        </select>
        {selectedCodeInfo && (
          <p className="text-xs text-gray-600 mt-2">
            Typical duration: {selectedCodeInfo.typicalDuration} minutes
            {selectedCodeInfo.allowsTelehealth && ' • Telehealth allowed'}
          </p>
        )}
      </div>

      {/* Place of Service */}
      <div>
        <label className="block text-sm font-bold text-gray-900 mb-2">
          Fee Schedule / Place of Service *
        </label>
        <select
          value={placeOfService}
          onChange={(e) => setPlaceOfService(e.target.value as PlaceOfService)}
          className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          {Object.entries(PLACE_OF_SERVICE).map(([code, description]) => (
            <option key={code} value={code}>
              {description}
            </option>
          ))}
        </select>
      </div>

      {/* Location Modifier (if telehealth) */}
      {isTelehealth && (
        <div>
          <label className="block text-sm font-bold text-gray-900 mb-2">
            Modifiers (Telehealth)
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => setLocationModifier('GT')}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                locationModifier === 'GT'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-green-500'
              }`}
            >
              GT
            </button>
            <button
              onClick={() => setLocationModifier('95')}
              className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                locationModifier === '95'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-green-500'
              }`}
            >
              95
            </button>
          </div>
        </div>
      )}

      {/* Time Worked */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-gray-900 mb-2">
            Start Time *
          </label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
              setShowWarning(false);
            }}
            className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-900 mb-2">End Time *</label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => {
              setEndTime(e.target.value);
              setShowWarning(false);
            }}
            className="w-full p-3 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      {/* Units Calculation Display */}
      {duration > 0 && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-5 h-5 text-blue-600" />
            <h4 className="text-sm font-bold text-blue-900">
              Units of Service Calculation
            </h4>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">{duration}</div>
              <div className="text-xs text-blue-700">Minutes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">=</div>
              <div className="text-xs text-blue-700">Equals</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{units}</div>
              <div className="text-xs text-blue-700">Units (15 min each)</div>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-3 text-center">
            Rounded to nearest 15-minute increment
          </p>
        </div>
      )}

      {/* Warning Message */}
      {showWarning && (
        <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-900 mb-1">
                Cannot Submit - Missing Required Information
              </p>
              <ul className="text-xs text-red-700 space-y-1">
                {!billingCode && <li>• Billing code is required</li>}
                {!placeOfService && <li>• Place of service is required</li>}
                {!startTime && <li>• Start time is required</li>}
                {!endTime && <li>• End time is required</li>}
                {startTime && endTime && endTime <= startTime && (
                  <li>• End time must be after start time</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        className={`w-full px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-3 ${
          billingCode && placeOfService && startTime && endTime
            ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg active:scale-95'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        <DollarSign className="w-6 h-6" />
        Complete Billing Entry
      </button>
    </div>
  );
};
